import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Gavel, Plus, Loader2, Send, FileText, AlertTriangle, CheckCircle2, Download, MailPlus, ShieldCheck, Search, RefreshCw, Link2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATO_STYLES = {
  BOZZA:                    { color: "#888", bg: "#88888822", label: "Bozza" },
  CORSIA_VERDE:             { color: "#4caf50", bg: "#4caf5022", label: "🟢 Corsia Verde" },
  CORSIA_ROSSA:             { color: "#ff0033", bg: "#ff003322", label: "🔴 Corsia Rossa" },
  INVIATA:                  { color: "#2196f3", bg: "#2196f322", label: "📤 Inviata" },
  ACCETTATA:                { color: "#03a9f4", bg: "#03a9f422", label: "✓ Accettata" },
  RINOTIFICATO_CON_SUCCESSO:{ color: "#00c853", bg: "#00c85322", label: "✅ Consegnata" },
  ERRORE_CONSEGNA:          { color: "#d32f2f", bg: "#d32f2f22", label: "❌ Errore" },
};

export default function Rinotifiche() {
  const loc = useLocation();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_stato: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manualForm, setManualForm] = useState({
    targa: "", data_violazione: "", ora_violazione: "12:00", numero_verbale: "",
    ente_accertatore: "", ente_comune: "", ente_pec: "", luogo_violazione: "",
    articolo_violato: "", importo_eur: 0,
  });

  const load = async () => {
    try {
      const [list, st] = await Promise.all([
        api.get("/rinotifiche", { params: { stato: filter || undefined } }),
        api.get("/rinotifiche/stats/summary"),
      ]);
      setItems(list.data.items || []);
      setStats(st.data);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  // Auto-open detail se ?id= in URL
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const id = params.get("id");
    if (id) openDetail(id);
  // eslint-disable-next-line
  }, [loc.search]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/rinotifiche/${id}`);
      setSelected(r.data.item);
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter(it =>
      (it.targa || "").toLowerCase().includes(s) ||
      (it.numero_verbale || "").toLowerCase().includes(s) ||
      (it.ente_accertatore || "").toLowerCase().includes(s) ||
      (it.customer_nome || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const createManual = async () => {
    setBusy(true);
    try {
      const r = await api.post("/rinotifiche/manual", manualForm);
      toast.success(`Pratica creata in ${r.data.stato}`);
      setShowManual(false);
      setManualForm({ targa: "", data_violazione: "", ora_violazione: "12:00", numero_verbale: "",
        ente_accertatore: "", ente_comune: "", ente_pec: "", luogo_violazione: "", articolo_violato: "", importo_eur: 0 });
      await load();
      openDetail(r.data.rinotifica_id);
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const genPackage = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/rinotifiche/${id}/package`);
      toast.success(`Pacchetto generato (${(r.data.size/1024).toFixed(1)} KB)` + (r.data.signed_pades ? " · firmato PAdES" : " · NON firmato (cert assente)"));
      openDetail(id);
      await load();
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setBusy(false); }
  };

  const downloadPackage = async (id) => {
    try {
      const r = await api.get(`/rinotifiche/${id}/package/download`);
      const a = document.createElement("a");
      a.href = `data:application/pdf;base64,${r.data.data_b64}`;
      a.download = r.data.filename;
      a.click();
    } catch (e) { toast.error("Errore download: " + e.message); }
  };

  const sendPec = async (id) => {
    if (!window.confirm("Inviare il pacchetto via PEC all'ente accertatore?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/rinotifiche/${id}/send`);
      toast.success(`PEC inviata a ${r.data.destinatario}`);
      openDetail(id);
      await load();
    } catch (e) { toast.error("Invio PEC fallito: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const notify = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/rinotifiche/${id}/notify-customer`);
      if (r.data.ok) toast.success("Email cliente inviata a " + r.data.sent_to);
      else toast.warning("Email non inviata: " + r.data.error);
      openDetail(id);
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setBusy(false); }
  };

  const approveManual = async (id) => {
    setBusy(true);
    try {
      await api.post(`/rinotifiche/${id}/approve-manual`);
      toast.success("Pratica approvata: passata a CORSIA VERDE");
      openDetail(id);
      await load();
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setBusy(false); }
  };

  const assignContract = async (id, contract_id) => {
    setBusy(true);
    try {
      await api.post(`/rinotifiche/${id}/assign-contract`, { contract_id });
      toast.success("Contratto assegnato — cliente identificato");
      openDetail(id);
      await load();
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="rinotifiche-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Gavel className="text-red-500"/> Rinotifiche Verbali
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Identificazione cliente con Regola Chiave Tripla · Generazione pacchetto PDF firmato PAdES · Invio PEC all&apos;ente · Tracking ricevute
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 border border-zinc-700" data-testid="reload-btn">
            <RefreshCw size={16}/> Aggiorna
          </button>
          <button onClick={() => setShowManual(true)} className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm flex items-center gap-2 font-semibold" data-testid="new-manual-btn">
            <Plus size={16}/> Nuova pratica manuale
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        <button onClick={() => setFilter("")} className={`p-3 rounded-lg border text-left ${!filter ? "border-red-500 bg-red-900/20" : "border-zinc-800 bg-zinc-900"}`} data-testid="filter-all">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs text-zinc-400">Totale pratiche</div>
        </button>
        {Object.entries(STATO_STYLES).map(([s, st]) => (
          <button key={s} onClick={() => setFilter(s)} className={`p-3 rounded-lg border text-left transition ${filter === s ? "border-white bg-zinc-800" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"}`} data-testid={`filter-${s.toLowerCase()}`}>
            <div className="text-2xl font-bold" style={{ color: st.color }}>{stats.by_stato?.[s] || 0}</div>
            <div className="text-xs text-zinc-400">{st.label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca targa, n° verbale, ente, cliente…"
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm focus:outline-none focus:border-red-500" data-testid="rin-search"/>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-zinc-500 text-center py-8"><Loader2 className="animate-spin inline mr-2"/></div>}
          {!loading && filtered.length === 0 && (
            <div className="text-zinc-500 text-center py-12 border border-dashed border-zinc-800 rounded-lg">
              <Gavel className="mx-auto mb-2 text-zinc-700" size={32}/>
              <div>Nessuna pratica.</div>
            </div>
          )}
          {filtered.map(it => {
            const st = STATO_STYLES[it.stato] || STATO_STYLES.BOZZA;
            const isActive = selected?.id === it.id;
            return (
              <button key={it.id} onClick={() => openDetail(it.id)} data-testid={`rin-item-${it.id}`}
                className={`w-full text-left p-3 rounded-lg border transition ${isActive ? "border-red-500 bg-zinc-800" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ color: st.color, background: st.bg }}>
                    {st.label}
                  </span>
                  <span className="text-[10px] text-zinc-500">{(it.created_at || "").slice(0,10)}</span>
                </div>
                <div className="text-sm font-bold">{it.targa || "TARGA?"} · n. {it.numero_verbale || "—"}</div>
                <div className="text-xs text-zinc-400 truncate">{it.ente_accertatore}</div>
                {it.customer_nome && <div className="text-xs text-zinc-500">→ {it.customer_nome}</div>}
                {!it.customer_nome && <div className="text-xs text-amber-500">⚠ Cliente non identificato</div>}
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg p-5 max-h-[70vh] overflow-y-auto" data-testid="rin-detail">
          {!selected && (
            <div className="text-zinc-500 text-center py-16">
              <Gavel className="mx-auto mb-3 text-zinc-700" size={48}/>
              <div>Seleziona una pratica per gestirla</div>
            </div>
          )}
          {selected && <DetailView item={selected} onGen={genPackage} onDownload={downloadPackage} onSend={sendPec} onNotify={notify} onApprove={approveManual} onAssign={assignContract} busy={busy} />}
        </div>
      </div>

      {/* Manual creation modal */}
      {showManual && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2"><Plus className="text-red-500"/> Nuova rinotifica manuale</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input data-testid="manual-targa" placeholder="Targa" value={manualForm.targa} onChange={e => setManualForm({...manualForm, targa: e.target.value.toUpperCase()})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-numero" placeholder="N° Verbale" value={manualForm.numero_verbale} onChange={e => setManualForm({...manualForm, numero_verbale: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-data" type="date" value={manualForm.data_violazione} onChange={e => setManualForm({...manualForm, data_violazione: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-ora" type="time" value={manualForm.ora_violazione} onChange={e => setManualForm({...manualForm, ora_violazione: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-ente" placeholder="Ente accertatore (es. Polizia Locale di MILANO)" value={manualForm.ente_accertatore} onChange={e => setManualForm({...manualForm, ente_accertatore: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded md:col-span-2"/>
              <input data-testid="manual-comune" placeholder="Comune" value={manualForm.ente_comune} onChange={e => setManualForm({...manualForm, ente_comune: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-pec" placeholder="PEC ente (vuoto = ricerca IPA)" value={manualForm.ente_pec} onChange={e => setManualForm({...manualForm, ente_pec: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-luogo" placeholder="Luogo violazione" value={manualForm.luogo_violazione} onChange={e => setManualForm({...manualForm, luogo_violazione: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded md:col-span-2"/>
              <input data-testid="manual-art" placeholder="Articolo violato (es. art. 142 c.7)" value={manualForm.articolo_violato} onChange={e => setManualForm({...manualForm, articolo_violato: e.target.value})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
              <input data-testid="manual-importo" type="number" step="0.01" placeholder="Importo €" value={manualForm.importo_eur} onChange={e => setManualForm({...manualForm, importo_eur: parseFloat(e.target.value || 0)})} className="p-2 bg-zinc-950 border border-zinc-700 rounded"/>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowManual(false)} className="px-4 py-2 bg-zinc-800 rounded">Annulla</button>
              <button onClick={createManual} disabled={busy || !manualForm.targa || !manualForm.data_violazione} className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded font-semibold disabled:opacity-50" data-testid="manual-submit">
                {busy ? <Loader2 className="animate-spin"/> : "Crea pratica"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailView({ item, onGen, onDownload, onSend, onNotify, onApprove, onAssign, busy }) {
  const [contracts, setContracts] = useState([]);
  const [contractsLoaded, setContractsLoaded] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [assignFilter, setAssignFilter] = useState("");

  const loadContracts = async () => {
    if (contractsLoaded) return;
    try {
      const r = await api.get("/contracts");
      setContracts(r.data || []);
      setContractsLoaded(true);
    } catch (e) { /* silent */ }
  };

  const openAssign = async () => {
    setAssignFilter(item.targa || "");
    await loadContracts();
    setShowAssign(true);
  };

  const conf = item.confidence || {};
  const st = STATO_STYLES[item.stato] || STATO_STYLES.BOZZA;
  const filteredContracts = (contracts || []).filter(c => {
    const f = assignFilter.toLowerCase();
    if (!f) return true;
    return (c.numero || "").toLowerCase().includes(f) ||
           (c.targa || "").toLowerCase().includes(f) ||
           (c.cliente_nome || "").toLowerCase().includes(f) ||
           (c.customer_nome || "").toLowerCase().includes(f);
  }).slice(0, 50);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-zinc-800">
        <div>
          <div className="text-xl font-bold">Verbale n. {item.numero_verbale || "—"}</div>
          <div className="text-xs text-zinc-400 mt-1">
            Targa <b>{item.targa}</b> · {item.data_violazione} {item.ora_violazione}
          </div>
        </div>
        <span className="text-sm px-3 py-1 rounded-full font-semibold" style={{ color: st.color, background: st.bg }}>
          {st.label}
        </span>
      </div>

      {/* Confidence */}
      <div className={`p-3 rounded-lg border ${conf.green_lane ? "bg-green-900/20 border-green-700" : "bg-red-900/20 border-red-700"}`}>
        <div className="text-sm font-bold flex items-center gap-2">
          {conf.green_lane ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
          {conf.green_lane ? "Corsia Verde — Automazione attiva" : "Corsia Rossa — Revisione manuale richiesta"}
        </div>
        <div className="text-xs text-zinc-400 mt-1">
          OCR: {((conf.ocr_confidence || 0)*100).toFixed(0)}% · Contratto univoco: {conf.has_unique_contract ? "✓" : "✗"}
        </div>
        {conf.reasons_blocked?.length > 0 && (
          <ul className="text-xs text-amber-300 mt-2 list-disc pl-4">
            {conf.reasons_blocked.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
      </div>

      {/* Dati verbale + cliente */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">DATI VERBALE</div>
          <div className="space-y-1 text-xs">
            <div><b>Ente:</b> {item.ente_accertatore || "—"}</div>
            <div><b>PEC ente:</b> {item.ente_pec || <span className="text-amber-500">⚠ non trovata</span>}
              {item.ente_pec_source && <span className="text-zinc-500 ml-1">({item.ente_pec_source})</span>}
            </div>
            <div><b>Luogo:</b> {item.luogo_violazione || "—"}</div>
            <div><b>Articolo:</b> {item.articolo_violato || "—"}</div>
            <div><b>Importo:</b> € {(item.importo_eur || 0).toFixed(2)}</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-500 font-semibold mb-2">CLIENTE IDENTIFICATO (Tripla Chiave)</div>
          {item.customer_nome ? (
            <div className="space-y-1 text-xs">
              <div><b>Nome:</b> {item.customer_nome}</div>
              <div><b>CF/PIVA:</b> {item.customer_cf || "—"}</div>
              <div><b>Contratto:</b> n. {item.contract_numero || item.contract_id?.slice(0,8)}</div>
              <div className="text-zinc-500">Match contratti trovati: {item.triple_key_matches}</div>
            </div>
          ) : (
            <div className="text-xs text-amber-400">
              ⚠ Cliente non identificato — {item.triple_key_warning || "verifica manualmente"}
              <button onClick={openAssign} className="mt-2 block px-2 py-1 bg-amber-700/40 hover:bg-amber-700/70 rounded text-xs flex items-center gap-1" data-testid="open-assign-contract">
                <Link2 size={12}/> Assegna contratto manualmente
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal assegna contratto */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setShowAssign(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-xl p-5 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Assegna contratto a questa rinotifica</h3>
              <button onClick={() => setShowAssign(false)} className="text-zinc-500 text-xl">×</button>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
              Cerca per numero contratto, targa o cliente. Verrà associato il cliente del contratto scelto.
            </div>
            <input autoFocus value={assignFilter} onChange={e => setAssignFilter(e.target.value)}
              placeholder={`es. ${item.targa} oppure numero contratto`}
              className="w-full p-2 bg-zinc-950 border border-zinc-700 rounded mb-3"
              data-testid="assign-search"/>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {filteredContracts.length === 0 && (
                <div className="text-center py-6 text-zinc-500 text-sm">Nessun contratto trovato</div>
              )}
              {filteredContracts.map(c => (
                <button key={c.id} onClick={() => { onAssign(item.id, c.id); setShowAssign(false); }}
                  data-testid={`assign-contract-${c.id}`}
                  className="w-full text-left p-2 bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 rounded text-xs">
                  <div className="font-bold">n. {c.numero || c.id?.slice(0,8)} · {c.targa || "?"}</div>
                  <div className="text-zinc-400">{c.customer_nome || c.cliente_nome || "?"} · {(c.data_inizio||'').slice(0,10)} → {(c.data_fine||'').slice(0,10)} · {c.stato}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pacchetto */}
      {item.package_filename && (
        <div className="bg-zinc-950 p-3 rounded border border-zinc-800 text-xs">
          <div className="font-semibold mb-1 flex items-center gap-2">
            <FileText size={14}/> {item.package_filename}
            {item.signed_pades && <ShieldCheck size={14} className="text-green-500" title="Firmato PAdES"/>}
          </div>
          <div className="text-zinc-500">{((item.package_size||0)/1024).toFixed(1)} KB · Generato: {item.package_generated_at?.slice(0,16).replace("T", " ")}</div>
          {item.sign_error && <div className="text-amber-500 mt-1">⚠ Firma PAdES non applicata: {item.sign_error}</div>}
        </div>
      )}

      {/* Tracking PEC */}
      {item.pec_sent_at && (
        <div className="bg-blue-900/20 border border-blue-700 rounded p-3 text-xs space-y-1">
          <div><b>📤 PEC inviata:</b> {item.pec_sent_at?.slice(0,16).replace("T", " ")} → {item.pec_sent_to}</div>
          {item.pec_receipts?.accettazione && <div className="text-blue-300">✓ Ricevuta accettazione: {item.pec_receipts.accettazione.received_at?.slice(0,16).replace("T", " ")}</div>}
          {item.pec_receipts?.consegna && <div className="text-green-400">✅ Ricevuta consegna: {item.pec_receipts.consegna.received_at?.slice(0,16).replace("T", " ")}</div>}
          {item.pec_receipts?.errore_consegna && <div className="text-red-400">❌ Errore consegna</div>}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-800">
        <button onClick={() => onGen(item.id)} disabled={busy} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm flex items-center gap-2 disabled:opacity-50" data-testid="action-gen-package">
          <FileText size={14}/> {item.package_b64 ? "Rigenera" : "Genera"} pacchetto PDF
        </button>
        {item.package_filename && (
          <button onClick={() => onDownload(item.id)} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm flex items-center gap-2" data-testid="action-download">
            <Download size={14}/> Scarica
          </button>
        )}
        {item.stato === "CORSIA_ROSSA" && (
          <button onClick={() => onApprove(item.id)} disabled={busy} className="px-3 py-2 bg-amber-700 hover:bg-amber-600 rounded text-sm flex items-center gap-2 disabled:opacity-50" data-testid="action-approve">
            <CheckCircle2 size={14}/> Approva manualmente (→ Verde)
          </button>
        )}
        {(item.stato === "CORSIA_VERDE" || item.stato === "BOZZA") && (
          <button onClick={() => onSend(item.id)} disabled={busy || !item.ente_pec}
            title={!item.ente_pec ? "Imposta prima la PEC dell'ente accertatore" : "Invia il pacchetto via PEC all'ente"}
            className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="action-send-pec">
            <Send size={14}/> Invia PEC all&apos;ente
          </button>
        )}
        <button onClick={() => onNotify(item.id)} disabled={busy || !item.customer_id}
          title={!item.customer_id ? "Identificare prima il cliente (assegna contratto)" : "Email di cortesia al cliente"}
          className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="action-notify">
          <MailPlus size={14}/> Notifica cliente
        </button>
      </div>

      {item.customer_notified_at && (
        <div className="text-xs text-green-400">
          ✓ Cliente notificato il {item.customer_notified_at?.slice(0,16).replace("T", " ")} ({item.customer_notified_email})
        </div>
      )}
    </div>
  );
}
